import { useGateValue } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button.tsx";
import { currentUserQuery } from "@/hooks/useUser.tsx";
import { CHANGE_ASSISTANT_NAME_CHAT_PROMPT } from "@/shared/assistantName.ts";
import { DEFAULT_ASSISTANT_NAME, RENAME_AGENT_GATE } from "@/types/contact.ts";

/**
 * The assistant's name in Settings → Assistant. Renaming happens in chat: the
 * button opens a new chat with the request already typed, so the assistant
 * checks the name the owner gives it.
 *
 * Refetches on every mount: the rename happens on the server mid-chat, so the
 * cached account would otherwise show the old name until it goes stale.
 */
export const AssistantNameCard = () => {
  const renameEnabled = useGateValue(RENAME_AGENT_GATE);
  const { data: userContext } = useQuery({ ...currentUserQuery(), refetchOnMount: "always" });
  const [, setLocation] = useLocation();
  const currentName = userContext?.user?.assistant?.name || DEFAULT_ASSISTANT_NAME;

  if (!renameEnabled) return null;

  return (
    <div>
      <h2 className="text-base font-semibold mb-2">Name</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Your assistant is called <span className="font-medium text-foreground">{currentName}</span>.
        To rename it, ask it in a new chat.
      </p>
      <Button
        variant="gradientOutline"
        className="duration-150"
        onClick={() =>
          setLocation(`/?input=${encodeURIComponent(CHANGE_ASSISTANT_NAME_CHAT_PROMPT)}`)
        }
        data-statsig-name="settings-assistant-name-change-in-chat"
      >
        Change name
      </Button>
    </div>
  );
};
